import React from 'react'

import {
  Accordion,
  AccordionSummary,
  Icon,
  Switch
} from '@clientbase/clientbase-library'
import {
  AccordionDetails,
  Box,
  Chip,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'

import { DatePicker } from 'components/DatePicker'

import { theme } from 'styles/theme'

export const REMINDER_OPTIONS = [
  { value: 0, label: 'No dia' },
  { value: 1, label: '1 dia' },
  { value: 2, label: '2 dias' },
  { value: 3, label: '3 dias' },
  { value: 5, label: '5 dias' },
  { value: 7, label: '7 dias' },
  { value: 10, label: '10 dias' },
  { value: 15, label: '15 dias' },
  { value: 30, label: '30 dias' }
]

interface Props {
  enabled: boolean
  onToggle: (enabled: boolean) => void
  expirationDate: Date | null
  onChangeExpirationDate: (date: Date | null) => void
  reminderDaysBefore: number
  onChangeReminderDaysBefore: (days: number) => void
}

const ExpirationAccordion: React.FC<Props> = ({
  enabled,
  onToggle,
  expirationDate,
  onChangeExpirationDate,
  reminderDaysBefore,
  onChangeReminderDaysBefore
}) => {
  return (
    <Accordion
      expanded={enabled}
      onChange={() => onToggle(!enabled)}
      sx={{
        mt: 1,
        border: '1px solid #E8EBE8',
        borderLeft: '3px solid #01f7c2',
        borderRadius: '8px !important',
        backgroundColor: '#FAFBFA',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: 0, mt: 1 }
      }}
    >
      <AccordionSummary
        hasSwitch
        expandIcon={
          <Switch
            checked={enabled}
            value={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        }
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="smBold">Data de expiração</Typography>
          <Tooltip
            enterTouchDelay={0}
            arrow
            placement="top"
            title="Defina uma data de expiração e receba uma notificação antes do vencimento"
          >
            <Box>
              <Icon
                icon="info"
                sx={{
                  fontSize: '1.2rem',
                  mt: '3px',
                  color: theme.palette.neutral[700]
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack gap={2}>
          <DatePicker
            value={expirationDate}
            onChange={(date: Date | null) => onChangeExpirationDate(date)}
            minDate={new Date()}
            renderInput={(props: any) => (
              <TextField
                {...props}
                size="small"
                fullWidth
                inputProps={{
                  ...props.inputProps,
                  placeholder: 'dd/mm/aaaa'
                }}
              />
            )}
          />
          <Box>
            <Typography
              variant="smBold"
              sx={{
                display: 'block',
                mb: 1,
                color: theme.palette.neutral[200]
              }}
            >
              Lembrar-me com antecedência:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {REMINDER_OPTIONS.map((opt) => {
                const selected = reminderDaysBefore === opt.value
                const daysUntilExpiration = expirationDate
                  ? Math.ceil(
                      (new Date(expirationDate).setHours(0, 0, 0, 0) -
                        new Date().setHours(0, 0, 0, 0)) /
                        (1000 * 60 * 60 * 24)
                    )
                  : null
                const disabled =
                  daysUntilExpiration === null ||
                  opt.value > daysUntilExpiration
                return (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    onClick={
                      disabled
                        ? undefined
                        : () => onChangeReminderDaysBefore(opt.value)
                    }
                    sx={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      pointerEvents: disabled ? 'none' : 'auto',
                      backgroundColor: selected
                        ? theme.palette.primary[500]
                        : 'transparent',
                      color: selected
                        ? theme.palette.primary[100]
                        : theme.palette.neutral[200],
                      border: `1px solid ${
                        selected
                          ? theme.palette.primary[300]
                          : theme.palette.neutral[600]
                      }`,
                      fontWeight: selected ? 600 : 400,
                      '&&:hover': {
                        backgroundColor: selected
                          ? theme.palette.primary[500]
                          : theme.palette.neutral[800]
                      }
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export default ExpirationAccordion
